import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "../components/Auth";
import { useFeedback } from "../components/Feedback";
import { Card, ErrorBox, Facts, Loading, PageHeader } from "../components/Page";
import { api } from "../lib/api";
import { day, monthLabel, num, usd, when } from "../lib/format";
import { useApi } from "../lib/useApi";

interface PnlRow { label: string; values: (number | null)[]; kind: "account" | "summary"; depth: number; group?: string }
interface Status {
  configured: boolean; environment: string | null; connected: boolean; company: string | null;
  connected_at: string | null; connected_by: string | null; refresh_expires_at: string | null;
  as_of: string | null; last_check_error: string | null;
  months: { month: string; books_usd: number | null; invoiced_usd: number | null; app_usd: number | null; difference: number | null }[];
  months_basis?: "income" | "invoices";
  invoice_check?: InvoiceCheck;
  open_credits?: { customer: string | null; balance: number }[];
  leftovers?: { count: number; total: number };
  pnl: { columns: string[]; rows: PnlRow[] } | null;
  open_invoices: { doc_number: string | null; customer: string | null; txn_date: string | null; due_date: string | null;
                   currency: string; total: number | null; balance: number | null; days_late: number | null }[];
  aging?: { bucket: string; balance: number }[]; open_total?: number;
}

interface CheckRow {
  status: "differs" | "sheet_only" | "books_only" | "date" | "agrees"; customer: string; notes: string[];
  sheet_number: string | null; sheet_date: string | null; sheet_amount: number | null;
  books_number: string | null; books_date: string | null; books_amount: number | null; books_balance: number | null;
  difference: number | null;
}
interface InvoiceCheck {
  year: number; counts: Record<CheckRow["status"], number>; totals: { sheet_only: number; books_only: number };
  not_in_books: { customer: string; count: number; amount: number }[]; rows: CheckRow[];
}
const STATUS: Record<CheckRow["status"], [string, string]> = {
  differs: ["Differs", "tone-warning"], sheet_only: ["Only in the sheet", "tone-info"],
  books_only: ["Only in QuickBooks", "tone-info"], date: ["Date differs", "tone-neutral"], agrees: ["Agrees", "tone-good"],
};
const NOTE: Record<string, string> = {
  amount: "amount", number: "invoice number", date: "date", "no date in the sheet": "no date in the sheet",
  "no amount in the sheet": "no amount in the sheet", "date in the future": "date in the sheet is in the future",
};

function CheckTable({ rows, testid }: { rows: CheckRow[]; testid?: string }) {
  return (
    <div className="table-scroll">
      <table className="grid compact" data-testid={testid}>
        <thead><tr><th>Customer</th><th>What</th><th>Sheet #</th><th>Sheet date</th><th className="n">Sheet</th>
          <th>Books #</th><th>Books date</th><th className="n">QuickBooks</th><th className="n">Difference</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i}>
            <td className="name">{r.customer}</td>
            <td><span className={`badge ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span>
              {r.status === "differs" && <span className="muted"> {r.notes.map((n) => NOTE[n] ?? n).join(", ")}</span>}</td>
            <td>{r.sheet_number ?? ""}</td><td>{day(r.sheet_date)}</td><td className="n">{usd(r.sheet_amount)}</td>
            <td>{r.books_number ?? ""}</td><td>{day(r.books_date)}</td><td className="n">{usd(r.books_amount)}</td>
            <td className={`n${r.difference && Math.abs(r.difference) >= 1 ? " down" : ""}`}>{r.difference == null || Math.abs(r.difference) < 1 ? "" : usd(r.difference)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

const RETURNED: Record<string, string> = {
  declined: "QuickBooks was not connected: the request was declined on Intuit's page.",
  state: "That return from Intuit did not match a Connect click from here, so it was ignored. Try Connect again.",
  admin: "Only an admin can connect QuickBooks.",
  token: "Intuit did not hand over the connection. Try Connect again.",
  intuit: "Intuit reported a problem. Try Connect again.",
};
// The P&L summary lines worth a row on this page; the rest is in QuickBooks.
const PNL_LINES = ["Income", "GrossProfit", "Expenses", "NetOperatingIncome", "NetIncome", "COGS"];

export function QuickBooks() {
  const { data: d, error, reload } = useApi<Status>("/quickbooks");
  const { user } = useAuth();
  const { toast, confirm } = useFeedback();
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const returned = params.get("error");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!d) return <Loading />;
  const admin = user?.role === "admin";

  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try { await fn(); if (done) toast(done); reload(); } catch (e) { toast((e as Error).message, "error"); } finally { setBusy(false); }
  };
  const connect = () => act(async () => {
    const r = await api.post<{ url: string }>("/quickbooks/connect");
    window.location.assign(r.url);
  });

  const header = (
    <PageHeader title="QuickBooks"
      sub="The books, read only: invoices, sales receipts, credit memos and the profit and loss, set against the sales the app already has."
      actions={d.connected ? <>
        <button className="btn btn-primary" disabled={busy} onClick={() => act(() => api.post("/quickbooks/sync"), "QuickBooks read again")}>
          {busy ? "Reading…" : "Read QuickBooks now"}</button>
        {admin && <button className="btn" disabled={busy} onClick={async () => {
          if (await confirm({ title: "Disconnect QuickBooks?", body: "The app stops reading the books and gives the connection back to Intuit. Nothing in QuickBooks changes.", confirm: "Disconnect", danger: true }))
            void act(() => api.post("/quickbooks/disconnect"), "Disconnected");
        }}>Disconnect</button>}
      </> : null} />
  );

  if (!d.connected) {
    return (
      <>
        {header}
        {returned && <p className="integration-error" role="alert">{RETURNED[returned] ?? RETURNED.intuit}</p>}
        <Card title="Connect to QuickBooks">
          <p>Connecting takes a minute and no password is shared with this app. You sign in on Intuit's own page, pick the
            company, and approve. The app then reads, every morning:</p>
          <ul>
            <li>invoices, sales receipts and credit memos from the start of last year;</li>
            <li>the profit and loss, month by month;</li>
            <li>what is still owed on each invoice.</li>
          </ul>
          <p>It never writes to QuickBooks: there is no code here that could, and a test keeps it that way. Disconnect any
            time, here or in QuickBooks.</p>
          {!d.configured ? (
            <p className="note muted" data-testid="qbo-not-configured">Waiting on our Intuit developer app, which has to exist before
              the button can work. Nothing for you to do yet.</p>
          ) : admin ? (
            <button className="btn btn-primary" disabled={busy} onClick={connect} data-testid="qbo-connect">Connect to QuickBooks</button>
          ) : (
            <p className="note muted">An admin connects QuickBooks.</p>
          )}
        </Card>
      </>
    );
  }

  const pnl = d.pnl;
  const income = d.months_basis === "income";
  const chk = d.invoice_check;
  const questions = chk ? chk.rows.filter((r) => r.status === "differs" || r.status === "sheet_only" || r.status === "books_only") : [];
  const dated = chk ? chk.rows.filter((r) => r.status === "date") : [];
  const agreed = chk ? chk.rows.filter((r) => r.status === "agrees") : [];
  const pnlRows = pnl ? pnl.rows.filter((r) => r.kind === "summary" && r.group && PNL_LINES.includes(r.group)) : [];
  return (
    <>
      {header}
      {params.get("connected") && <p className="integration-note" role="status">Connected to {d.company ?? "QuickBooks"}.</p>}
      {d.last_check_error && <p className="integration-error" role="alert">Last read failed: {d.last_check_error}</p>}
      <Card>
        <Facts items={[["Company", d.company], ["Read as of", d.as_of ? day(d.as_of) : "not yet"],
          ["Connected", d.connected_at ? `${when(d.connected_at)}${d.connected_by ? ` by ${d.connected_by}` : ""}` : null],
          ["Connection good until", d.refresh_expires_at ? day(d.refresh_expires_at) : null],
          ...(d.environment === "sandbox" ? [["Mode", "Intuit's sandbox (test company)"] as [string, string]] : [])]} />
      </Card>

      {d.months.length > 0 && (
        <Card title="The books against the app, month by month">
          <div className="table-scroll">
            <table className="grid compact" data-testid="qbo-months">
              <thead><tr><th>Month</th><th className="n">{income ? "QuickBooks income" : "QuickBooks"}</th>
                {income && <th className="n">Invoiced in QuickBooks</th>}
                <th className="n">The app's sales</th><th className="n">Difference</th></tr></thead>
              <tbody>{d.months.map((m) => (
                <tr key={m.month}><td>{monthLabel(m.month)}</td><td className="n">{usd(m.books_usd)}</td>
                  {income && <td className="n muted">{usd(m.invoiced_usd)}</td>}
                  <td className="n">{usd(m.app_usd)}</td>
                  <td className={`n${m.difference && Math.abs(m.difference) >= 1 ? " down" : ""}`}>{m.difference == null ? "" : usd(m.difference)}</td></tr>
              ))}</tbody>
            </table>
          </div>
          {income ? (
            <p className="note muted">QuickBooks income: the Total Income line of the profit and loss for the month. Invoiced: only
              the customers billed from QuickBooks directly (River Valley, Dufry, Starboard…); MHW's US sales reach the books
              another way, so this column is always the smaller. The app: Zach's sales report and MHW's US figures. A difference
              is a question to ask (a sale booked in another month, an adjustment, excise), not an error in either.</p>
          ) : (
            <p className="note muted">QuickBooks: invoices and sales receipts less credit memos, by their date, in dollars. The app: Zach's
              sales report and MHW's US figures. A difference is a question to ask, not an error in either.</p>
          )}
        </Card>
      )}

      {chk && (
        <Card title={`Invoices: Zach's sales sheet against QuickBooks, ${chk.year}`}>
          <p className="note" data-testid="qbo-check-summary">
            {chk.counts.agrees + chk.counts.date} agree{chk.counts.date ? ` (${chk.counts.date} with a different date)` : ""}
            {" · "}{chk.counts.differs} differ
            {" · "}{chk.counts.sheet_only} only in the sheet ({usd(chk.totals.sheet_only)})
            {" · "}{chk.counts.books_only} only in QuickBooks ({usd(chk.totals.books_only)})
          </p>
          {chk.not_in_books.length > 0 && (
            <p className="note muted">Not invoiced from QuickBooks at all: {chk.not_in_books.map((g) =>
              `${g.customer} (${g.count} in the sheet, ${usd(g.amount)})`).join(", ")}. These are booked some other way, or not yet.</p>
          )}
          {questions.length > 0 && <CheckTable rows={questions} testid="qbo-check" />}
          {dated.length > 0 && (
            <details className="small">
              <summary>Same invoice and amount, different date ({dated.length})</summary>
              <CheckTable rows={dated} />
            </details>
          )}
          {agreed.length > 0 && (
            <details className="small">
              <summary>Agree ({agreed.length})</summary>
              <CheckTable rows={agreed} />
            </details>
          )}
          <p className="note muted">Matched by customer, then invoice number, then the same amount close in time. The sheet is Zach's
            sales report; QuickBooks is what was invoiced from the books. A row here is a question for him or the bookkeeper: a typo,
            an invoice not yet raised, or one raised twice. An undated row in the sheet is often an order not invoiced yet.</p>
        </Card>
      )}

      {pnl && pnlRows.length > 0 && (
        <Card title="Profit and loss by month">
          <div className="table-scroll">
            <table className="grid compact" data-testid="qbo-pnl">
              <thead><tr><th />{pnl.columns.map((c) => <th key={c} className="n">{c}</th>)}</tr></thead>
              <tbody>{pnlRows.map((r) => (
                <tr key={r.label} className={r.group === "NetIncome" ? "totals" : undefined}><td className="name">{r.label}</td>
                  {r.values.map((v, i) => <td key={i} className="n">{v == null ? "" : usd(v)}</td>)}</tr>
              ))}</tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title={`Still owed${d.open_total ? `: ${usd(d.open_total)}` : ""}`}>
        {d.aging && d.aging.length > 0 && (
          <p className="note">{d.aging.map((a) => `${a.bucket} ${usd(a.balance)}`).join(" · ")}</p>
        )}
        {d.open_credits && d.open_credits.length > 0 && (
          <p className="note" data-testid="qbo-credits">Credit notes not used yet, which take that much off what is owed:{" "}
            {d.open_credits.map((c) => `${c.customer ?? "unnamed"} ${usd(c.balance)}`).join(", ")}.</p>
        )}
        {d.leftovers && d.leftovers.count > 0 && (
          <p className="note muted" data-testid="qbo-leftovers">Invoices with under $100 left on them and over 60 days late:{" "}
            {d.leftovers.count} ({usd(d.leftovers.total)}). Usually bank fees or a short payment nobody wrote off; one tidy-up for
            the bookkeeper.</p>
        )}
        {d.open_invoices.length ? (
          <div className="table-scroll">
            <table className="grid compact" data-testid="qbo-open">
              <thead><tr><th>Invoice</th><th>Customer</th><th>Date</th><th>Due</th><th className="n">Total</th><th className="n">Owed</th><th className="n">Days late</th></tr></thead>
              <tbody>{d.open_invoices.map((i) => (
                <tr key={`${i.doc_number}-${i.customer}`}><td>{i.doc_number ?? ""}</td><td>{i.customer ?? ""}</td><td>{day(i.txn_date)}</td>
                  <td>{day(i.due_date)}</td><td className="n">{usd(i.total)}</td><td className="n">{usd(i.balance)}</td>
                  <td className={`n${i.days_late ? " down" : ""}`}>{i.days_late ? num(i.days_late) : ""}</td></tr>
              ))}</tbody>
            </table>
          </div>
        ) : <p className="note muted">Nothing owed on any invoice.</p>}
      </Card>
    </>
  );
}
